import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { authFetch, API_BASE } from "../auth";
import "./TrailingSell.css";

const API = API_BASE;
const QUICK_AMOUNTS = [0.05, 0.10, 0.25, 0.50, 1.00];
const COND_POLL_MS = 5000;

const STATUS_LABELS = {
  active: "Active",
  filled: "Filled",
  cancelled: "Cancelled",
  error: "Error",
};

const LEG_STATUS_LABELS = {
  watching: "Watching",
  trailing: "Trailing",
  sold: "Sold",
  linked_sold: "Linked Sell",
};

/* ── Active Conditions panel ── */

function ActiveConditions({ orders, onCancel, onClear }) {
  if (orders.length === 0) return null;
  const hasCompleted = orders.some((o) => o.status !== "active");

  return (
    <section className="tsCard tsCondPanel">
      <div className="tsCard__head">
        <h2 className="tsCard__title">Conditional Monitors</h2>
        {hasCompleted && (
          <button type="button" className="tsGhostBtn" onClick={onClear}>
            Clear completed
          </button>
        )}
      </div>
      <div className="tsCondList">
        {orders.map((o) => (
          <div className={`tsCondRow tsCondRow--${o.status}`} key={o.id}>
            <div className="tsCondRow__ticker">{o.ticker}</div>
            <div className={`tsCondRow__badge tsCondRow__badge--${o.status}`}>
              {STATUS_LABELS[o.status] || o.status}
            </div>
            <div className="tsCondRow__msg">{o.message}</div>

            {o.legs && (
              <div className="tsCondRow__legs">
                {Object.entries(o.legs).map(([cid, leg]) => (
                  <span
                    key={cid}
                    className={`tsLegPill tsLegPill--${leg.status}`}
                  >
                    {leg.role || leg.right || cid}: {LEG_STATUS_LABELS[leg.status] || leg.status}
                  </span>
                ))}
              </div>
            )}

            <div className="tsCondRow__time">{o.created_at}</div>

            {o.status === "active" && (
              <button
                type="button"
                className="tsGhostBtn tsGhostBtn--sm"
                onClick={() => onCancel(o.id)}
              >
                Cancel
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

ActiveConditions.propTypes = {
  orders: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

/* ── Main component ── */

export default function TrailingSell() {
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [trailAmount, setTrailAmount] = useState("0.10");
  const [triggerPrice, setTriggerPrice] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });

  // ── per-leg conditional settings ──
  const [legSettings, setLegSettings] = useState({});
  // legSettings[conId] = { role: "", trailAmount: "", pctGainTarget: "" }

  // ── per-ticker stock-price condition ──
  const [tickerCond, setTickerCond] = useState({});
  // tickerCond[ticker] = { condition: "", target: "" }

  // ── conditional order monitoring ──
  const [condOrders, setCondOrders] = useState([]);
  const pollRef = useRef(null);

  /* ── data fetching ── */

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API}/positions/`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch");
      setPositions(data.positions || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCondStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/conditional-trail/status/`);
      if (res.ok) {
        const data = await res.json();
        setCondOrders(data.orders || []);
      }
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchPositions();
    fetchCondStatus();
    pollRef.current = setInterval(fetchCondStatus, COND_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchPositions, fetchCondStatus]);

  /* ── helpers ── */

  const getLeg = (conId) =>
    legSettings[conId] || { role: "", trailAmount: "", pctGainTarget: "" };

  const setLeg = (conId, field, value) => {
    setLegSettings((prev) => ({
      ...prev,
      [conId]: { ...getLegFromPrev(prev, conId), [field]: value },
    }));
  };

  const getCond = (ticker) =>
    tickerCond[ticker] || { condition: "", target: "" };

  const setCondField = (ticker, field, value) => {
    setTickerCond((prev) => ({
      ...prev,
      [ticker]: { ...(prev[ticker] || { condition: "", target: "" }), [field]: value },
    }));
  };

  /* ── immediate trailing (existing) ── */

  const toggleSelect = (conId) => {
    setSelected((p) => ({ ...p, [conId]: !p[conId] }));
  };

  const selectAllForTicker = (ticker) => {
    const items = positions[ticker] || [];
    const allSelected = items.every((p) => selected[p.conId]);
    const updates = {};
    items.forEach((p) => { updates[p.conId] = !allSelected; });
    setSelected((prev) => ({ ...prev, ...updates }));
  };

  const handleTrailSelected = async () => {
    const orders = [];
    Object.keys(positions).forEach((t) => {
      positions[t].forEach((p) => {
        if (selected[p.conId]) {
          orders.push({
            conId: p.conId,
            quantity: p.quantity,
            trailAmount: parseFloat(trailAmount),
            triggerPrice: triggerPrice ? parseFloat(triggerPrice) : null,
          });
        }
      });
    });

    if (orders.length === 0) {
      setStatus({ state: "error", message: "No positions selected" });
      return;
    }

    setStatus({ state: "loading", message: "Placing trailing orders\u2026" });
    try {
      const res = await authFetch(`${API}/trailing-sell/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orders }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      const placed = (data.results || []).filter((r) => r.status === "placed").length;
      setStatus({ state: "success", message: `${placed} trailing order(s) placed` });
      setSelected({});
    } catch (e) {
      setStatus({ state: "error", message: e.message });
    }
  };

  const handleTrailAllLegs = async (ticker) => {
    setStatus({ state: "loading", message: `Trailing all ${ticker} legs\u2026` });
    try {
      const res = await authFetch(`${API}/trail-all-legs/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticker, trailAmount: parseFloat(trailAmount) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      const placed = (data.results || []).filter((r) => r.status === "placed").length;
      setStatus({ state: "success", message: `${placed} trailing order(s) placed for ${ticker}` });
    } catch (e) {
      setStatus({ state: "error", message: e.message });
    }
  };

  /* ── conditional monitoring ── */

  const handleMonitorTicker = async (ticker) => {
    const items = positions[ticker] || [];
    const cond = getCond(ticker);

    const legs = {};
    items.forEach((p) => {
      const ls = getLeg(p.conId);
      legs[String(p.conId)] = {
        conId: p.conId,
        quantity: p.quantity,
        trailAmount: parseFloat(ls.trailAmount) || parseFloat(trailAmount) || 0.10,
        pctGainTarget: parseFloat(ls.pctGainTarget) || 0,
        role: ls.role || "",
        right: p.right || "",
        avgCost: p.avgCost,
        status: "watching",
      };
    });

    const hasCondition = cond.condition && parseFloat(cond.target) > 0;
    const hasPctTarget = Object.values(legs).some((l) => l.pctGainTarget > 0);

    if (!hasCondition && !hasPctTarget) {
      setStatus({ state: "error", message: `No conditions set for ${ticker}` });
      return;
    }

    setStatus({ state: "loading", message: `Submitting ${ticker} monitor\u2026` });
    try {
      const res = await authFetch(`${API}/conditional-trail/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticker,
          stock_price_condition: cond.condition || "",
          stock_price_target: parseFloat(cond.target) || 0,
          legs,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setStatus({ state: "success", message: `${ticker} monitor started (${data.order_id})` });
      fetchCondStatus();
    } catch (e) {
      setStatus({ state: "error", message: e.message });
    }
  };

  const handleCondCancel = async (orderId) => {
    setStatus({ state: "loading", message: "Cancelling monitor…" });
    try {
      const res = await authFetch(`${API}/conditional-trail/cancel/${orderId}/`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Cancel failed");
      setStatus({
        state: "success",
        message: data.message || "Monitor cancelled.",
      });
      fetchCondStatus();
    } catch (e) {
      setStatus({ state: "error", message: e.message });
    }
  };

  const handleCondClear = async () => {
    try {
      await authFetch(`${API}/conditional-trail/clear/`, { method: "POST" });
      fetchCondStatus();
    } catch { /* silent */ }
  };

  const tickers = Object.keys(positions).sort();

  return (
    <div>
      <div className="page-header">
        <h2 className="page-header__title">Trailing Sell</h2>
        <p className="page-header__subtitle">
          Immediate trailing stops or conditional monitors with stock-price / % gain triggers.
        </p>
      </div>

      {/* ── Conditional monitors panel ── */}
      <ActiveConditions
        orders={condOrders}
        onCancel={handleCondCancel}
        onClear={handleCondClear}
      />

      {/* ── Global trail settings ── */}
      <section className="tsCard">
        <div className="tsCard__head">
          <h2 className="tsCard__title">Quick Trail Settings</h2>
          <button className="tsGhostBtn" onClick={fetchPositions} disabled={loading}>
            {loading ? "Loading\u2026" : "\u27F3 Refresh"}
          </button>
        </div>

        <div className="tsControls">
          <div className="tsField">
            <label className="tsField__label">Default Trail Amount ($)</label>
            <input
              className="tsInput"
              type="number"
              step="0.01"
              min="0.01"
              value={trailAmount}
              onChange={(e) => setTrailAmount(e.target.value)}
            />
            <div className="tsQuickBtns">
              {QUICK_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  className={`tsQuickBtn ${parseFloat(trailAmount) === amt ? "tsQuickBtn--active" : ""}`}
                  onClick={() => setTrailAmount(amt.toFixed(2))}
                >
                  ${amt.toFixed(2)}
                </button>
              ))}
            </div>
          </div>

          <div className="tsField">
            <label className="tsField__label">Trigger Price (optional, immediate only)</label>
            <input
              className="tsInput"
              type="number"
              step="0.01"
              placeholder="Leave blank for current price"
              value={triggerPrice}
              onChange={(e) => setTriggerPrice(e.target.value)}
            />
          </div>
        </div>
      </section>

      {error && <div className="tsError">{error}</div>}

      {/* ── Positions by ticker ── */}
      {tickers.map((ticker) => {
        const items = positions[ticker];
        const allSelected = items.every((p) => selected[p.conId]);
        const cond = getCond(ticker);

        return (
          <section className="tsCard" key={ticker}>
            <div className="tsCard__head">
              <div className="tsTickerInfo">
                <span className="tsTicker">{ticker}</span>
                <span className="tsCount">
                  {items.length} position{items.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="tsTickerActions">
                <button className="tsSelectAllBtn" onClick={() => selectAllForTicker(ticker)}>
                  {allSelected ? "Deselect All" : "Select All"}
                </button>
                <button className="tsTrailAllBtn" onClick={() => handleTrailAllLegs(ticker)}>
                  Trail All Legs
                </button>
                <button className="tsMonitorBtn" onClick={() => handleMonitorTicker(ticker)}>
                  Monitor
                </button>
              </div>
            </div>

            {/* ── Stock price condition ── */}
            <div className="tsStockCond">
              <span className="tsStockCond__label">Stock Price Trigger:</span>
              <select
                className="tsStockCond__select"
                value={cond.condition}
                onChange={(e) => setCondField(ticker, "condition", e.target.value)}
              >
                <option value="">None</option>
                <option value=">">&gt; Above</option>
                <option value="<">&lt; Below</option>
              </select>
              {cond.condition && (
                <input
                  className="tsStockCond__input"
                  type="number"
                  step="0.01"
                  placeholder="Target price"
                  value={cond.target}
                  onChange={(e) => setCondField(ticker, "target", e.target.value)}
                />
              )}
              {!cond.condition && (
                <span className="tsStockCond__hint">
                  Select a condition to trigger trail on all legs when stock price is hit
                </span>
              )}
            </div>

            {/* ── Positions table ── */}
            <div className="tsTableWrap">
              <table className="tsTable">
                <thead>
                  <tr>
                    <th></th>
                    <th>Role</th>
                    <th>Type</th>
                    <th>Strike</th>
                    <th>Expiry</th>
                    <th>Qty</th>
                    <th>Avg Cost</th>
                    <th>Price</th>
                    <th>P&amp;L ($)</th>
                    <th>P&amp;L (%)</th>
                    <th>Trail $</th>
                    <th>% Target</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => {
                    const ls = getLeg(p.conId);
                    return (
                      <tr
                        key={p.conId}
                        className={selected[p.conId] ? "tsRow--selected" : ""}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={!!selected[p.conId]}
                            onChange={() => toggleSelect(p.conId)}
                          />
                        </td>
                        <td>
                          <select
                            className="tsInlineSelect"
                            value={ls.role}
                            onChange={(e) => setLeg(p.conId, "role", e.target.value)}
                          >
                            <option value="">--</option>
                            <option value="core">Core</option>
                            <option value="lotto">Lotto</option>
                            <option value="hedge">Hedge</option>
                          </select>
                        </td>
                        <td>{p.right || p.secType}</td>
                        <td>{p.strike ? p.strike.toFixed(1) : "\u2014"}</td>
                        <td>{p.expiry || "\u2014"}</td>
                        <td>{p.quantity}</td>
                        <td>{p.avgCost != null ? p.avgCost.toFixed(2) : "\u2014"}</td>
                        <td>{p.currentPrice != null ? p.currentPrice.toFixed(2) : "\u2014"}</td>
                        <td className={p.unrealizedPnL >= 0 ? "tsGreen" : "tsRed"}>
                          {p.unrealizedPnL != null
                            ? `${p.unrealizedPnL >= 0 ? "+" : ""}${p.unrealizedPnL.toFixed(2)}`
                            : "\u2014"}
                        </td>
                        <td className={p.unrealizedPnLPct >= 0 ? "tsGreen" : "tsRed"}>
                          {p.unrealizedPnLPct != null
                            ? `${p.unrealizedPnLPct >= 0 ? "+" : ""}${p.unrealizedPnLPct.toFixed(1)}%`
                            : "\u2014"}
                        </td>
                        <td>
                          <input
                            className="tsInlineInput"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={trailAmount}
                            value={ls.trailAmount}
                            onChange={(e) => setLeg(p.conId, "trailAmount", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="tsInlineInput"
                            type="number"
                            step="1"
                            min="0"
                            placeholder="0"
                            value={ls.pctGainTarget}
                            onChange={(e) => setLeg(p.conId, "pctGainTarget", e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {items.length >= 3 && (
              <div className="tsLinkedHint">
                Linked sell active: Core triggers Hedge sell, Hedge triggers Core sell.
                Assign roles above for linked logic to apply.
              </div>
            )}
          </section>
        );
      })}

      {!loading && tickers.length === 0 && !error && (
        <div className="tsEmpty">No open positions found.</div>
      )}

      {/* ── Action Bar ── */}
      <div className="tsActionBar">
        <div className={`tsStatus tsStatus--${status.state}`}>
          {status.state === "idle" ? (
            <span className="tsStatus__muted">Ready</span>
          ) : (
            status.message
          )}
        </div>
        <button
          className="tsPrimaryBtn"
          onClick={handleTrailSelected}
          disabled={
            status.state === "loading" ||
            Object.values(selected).filter(Boolean).length === 0
          }
        >
          {status.state === "loading"
            ? "Placing\u2026"
            : `Trail Selected (${Object.values(selected).filter(Boolean).length})`}
        </button>
      </div>
    </div>
  );
}

/* helper used inside setLegSettings callback (needs prev, not stale closure) */
function getLegFromPrev(prev, conId) {
  return prev[conId] || { role: "", trailAmount: "", pctGainTarget: "" };
}
